import { Grid, Stack, Typography } from "@mui/material";
import {
  BaseSyntheticEvent,
  lazy,
  useMemo,
  Suspense,
  useState,
  useEffect,
} from "react";
import { SolutionMap } from "@labs/shared/types/Solution.ts";
import { useForm, useWatch } from "react-hook-form";
import { Outlet, useNavigate, useLocation } from "react-router";
import { useLocalSolutionMapState } from "../util/useLocalSolutionMapState.ts";
import { useLocalTargetState } from "../util/useLocalTargetState.ts";
import { analyzePlan } from "../util/planAnalysis/analyzePlan.ts";
import { useLocalBaselineEmissionsState } from "../util/useLocalBaselineEmissionsState.ts";
import { OnboardingStepper } from "../components/OnboardingStepper.tsx";
import { onboardingSteps } from "../data/onboardingSteps.ts";
import { LoadingIndicator } from "../components/LoadingIndicator.tsx";
import { trackEvent } from "../util/analytics";
import { EventCategory } from "@labs/shared/types/EventCategory.ts";
import {
  orderedSolutionPages,
  solutionsNavigationDataByKey,
} from "../data/solutionsNavigationData";
import PlanBySourceChart from "../charts/PlanBySourceChart.tsx";
import { HeroHeader } from "../components/HeroHeader.tsx";
import { NavigationButtons } from "../components/NavigationButtons.tsx";
import { LabsCard } from "../components/LabsCard.tsx";
import { DashboardHighlight } from "../components/DashboardHighlight.tsx";
import { validatePlan } from "../util/planAnalysis/validatePlan.ts";
import { ErrorAlert } from "../components/ErrorAlert.tsx";
import { TotalPercentageDisplay } from "../components/TotalPercentageDisplay.tsx";
import { getTotalElectricityPurchase } from "../util/planAnalysis/getTotalElectricityPurchase.ts";
import { useLocalUserState } from "../util/useLocalUserState.ts";
import { User } from "@labs/shared/types/User.ts";

const GapWaterfallChart = lazy(() => import("../charts/GapWaterfallChart"));

const SolutionsContainer = () => {
  const [solutionMap, setSolutionMap] = useLocalSolutionMapState();
  const [target] = useLocalTargetState();
  const [baselineEmissions] = useLocalBaselineEmissionsState();
  const { control, handleSubmit: reactHookFormCreateHandleSubmit } =
    useForm<SolutionMap>({
      mode: "onChange",
      defaultValues: solutionMap,
    });
  const [, setUser] = useLocalUserState();
  const solutionMapFormValues = useWatch({ control });
  const analyzedPlan = useMemo(
    () => analyzePlan(solutionMapFormValues, target, baselineEmissions),
    [baselineEmissions, solutionMapFormValues, target],
  );
  const totalElectricityPurchasePercent = useMemo(
    () => getTotalElectricityPurchase(solutionMapFormValues) * 100,
    [solutionMapFormValues],
  );
  const planValidationErrors = useMemo(
    () => validatePlan(solutionMapFormValues),
    [solutionMapFormValues],
  );
  const navigate = useNavigate();

  const [hoveredCharts] = useState(new Set<string>());

  const location = useLocation();
  const currentKey = location.pathname.split("/").pop() || "";
  const currentIndex = orderedSolutionPages.indexOf(currentKey);
  const currentNavigationItem = solutionsNavigationDataByKey[currentKey];

  useEffect(() => {
    setUser((prevUser: User) => {
      const viewedPlanComponents = new Set(prevUser.viewedPlanComponents || []);
      viewedPlanComponents.add(currentKey);
      return {
        ...prevUser,
        viewedPlanComponents: Array.from(viewedPlanComponents),
      };
    });
  }, [currentKey, setUser]);

  const handleBack = () => {
    if (currentIndex <= 0) {
      navigate("/plan");
    } else {
      navigate(`/plan/entry/${orderedSolutionPages[currentIndex - 1]}`);
    }
  };

  const handleNext = (data: SolutionMap) => {
    setSolutionMap(data);

    if (currentIndex === orderedSolutionPages.length - 1) {
      trackEvent(EventCategory.Plan, "Complete");
      navigate("/dashboard");
    } else {
      navigate(`/plan/entry/${orderedSolutionPages[currentIndex + 1]}`);
    }
  };

  // look at submit button name to determine which handler to call. both back to dashboard and next are submit
  // buttons. next saves form data to state and moves user to the next solution page; back to dashboard
  // also saves form data to state but then navigates user back to the dashboard
  const onSubmit = (e?: BaseSyntheticEvent): Promise<void> => {
    e?.preventDefault();
    const nativeEvent = e?.nativeEvent as SubmitEvent;
    const button = nativeEvent?.submitter as HTMLButtonElement;
    const buttonName = button?.name;

    const submitHandler =
      buttonName === "backToSolutions" ? handleBackToSolutions : handleNext;

    const reactHookFormSubmitFn =
      reactHookFormCreateHandleSubmit(submitHandler);
    return reactHookFormSubmitFn(e);
  };

  const handleBackToSolutions = (data: SolutionMap) => {
    setSolutionMap(data);
    trackEvent(EventCategory.Plan, "Back to Solutions");
    navigate("/plan");
  };

  const validationErrorsForCurrentPage = planValidationErrors.filter((error) =>
    error.applicableSolutionKeys.includes(currentKey),
  );

  return (
    <Stack direction={"column"} spacing={4}>
      <OnboardingStepper activeStep={2} steps={onboardingSteps} />
      <form onSubmit={onSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <HeroHeader
              title={`${currentNavigationItem.category}: ${currentNavigationItem.name}`}
              description={currentNavigationItem.description || ""}
              backgroundImage={currentNavigationItem.heroBackgroundImage}
              showCalculateLink
            />
          </Grid>
          <Grid size={12}>
            {validationErrorsForCurrentPage.map((error) => (
              <ErrorAlert title={error.description} />
            ))}
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <Outlet context={control} />
            {currentKey === "scope2BuyClean" && (
              <TotalPercentageDisplay
                totalPercentage={totalElectricityPurchasePercent}
                allowUnder100
                sx={{ mb: 2 }}
              />
            )}
            <NavigationButtons
              backEnabled
              nextEnabled={validationErrorsForCurrentPage.length === 0}
              onBackClick={handleBack}
              // only first validation error is shown next to button
              validationError={validationErrorsForCurrentPage[0]}
              showBackToSolutionsButton
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Stack direction={"column"} spacing={{ xs: 4, md: 2 }}>
              <Stack direction={"row"} spacing={2}>
                <DashboardHighlight
                  target={target}
                  analyzedPlan={analyzedPlan}
                  compactDisplay
                />
              </Stack>
              <LabsCard>
                <Typography variant={"body1"} color={"primary.dark"}>
                  Emissions by source
                </Typography>
                <Suspense fallback={<LoadingIndicator />}>
                  <PlanBySourceChart
                    analyzedPlan={analyzedPlan}
                    showEditLink={false}
                    hoveredCharts={hoveredCharts}
                    analyticsCategory={EventCategory.Plan}
                  />
                </Suspense>
              </LabsCard>
              <LabsCard>
                <Typography variant={"body1"} color={"primary.dark"}>
                  Emissions reduction plan
                </Typography>
                <Suspense fallback={<LoadingIndicator />}>
                  <GapWaterfallChart
                    analyzedPlan={analyzedPlan}
                    showEditLink={false}
                    target={target}
                    hoveredCharts={hoveredCharts}
                    analyticsCategory={EventCategory.Plan}
                  />
                </Suspense>
              </LabsCard>
            </Stack>
          </Grid>
        </Grid>
      </form>
    </Stack>
  );
};

export default SolutionsContainer;
